import React, { RefObject } from "react";
import { FormField, Input, Textarea } from "@amzn/awsui-components-react-v3";
import { FieldSchemaDto } from "@amzn/crowd-front-end-common-library";
import FormFieldSelectWrapper from "../FormFieldSelectWrapper/FormFieldSelectWrapper";
import { useFormState, useFormDispatch } from "../formContext";
import { AutoSuggestItem } from "../../search/AutoSuggestResponse";
import { PeoplePicker } from "../../search";
import FormFieldInputWrapper from "../FormFieldInputWrapper/FormFieldInputWrapper";
import { actions } from "../formSlice";
import defaultValidationErrorTextFormatter from "../defaultValidationErrorTextFormatter";
import { getBusinessTitleFromSuggestItem } from "../../search/suggestionsPicker/utils/peoplePickerUtils";
import FormFieldMultiSelectWrapper from "../FormFieldMultiSelectWrapper/FormFieldMultiSelectWrapper";
import TeamPicker from "../../search/suggestionsPicker/TeamPicker";
import { getIdFromSuggestItem as getTeamIdFromSuggestItem } from "../../search/suggestionsPicker/utils/teamPickerUtils";

interface FormFieldProps {
  field: FieldSchemaDto;
  customLabel?: string | JSX.Element;
  stretch?: boolean;
  dispatchOnValueUpdate?: boolean;
}

const FormFieldRenderer: React.FC<FormFieldProps> = ({
  field,
  customLabel,
  stretch = false,
  dispatchOnValueUpdate,
}): JSX.Element => {
  const formState = useFormState<Record<string, any>>();
  const dispatch = useFormDispatch();

  const {
    disabled,
    propertyName,
    placeholder = "",
    allowedValues,
    fetchAllowedValues,
    validationRules,
  } = field;

  const renderInput = (): JSX.Element => {
    switch (field.propertyType) {
      case "select<string>":
      case "select<number>": {
        // short term fix, need long term form schema change.
        const filterType = fetchAllowedValues ? "auto" : "none";

        return (
          <FormFieldSelectWrapper
            value={formState.currentValue[propertyName]}
            dispatch={dispatch}
            metricsState={formState.metrics}
            propertyName={propertyName}
            dropdownOptionValues={(allowedValues as (string | number)[]) || []}
            supportFetch={fetchAllowedValues}
            filteringType={filterType}
            isNumber={field.propertyType.includes("number")}
            placeholder={placeholder}
            selectedOption={
              formState.currentValue[propertyName]
                ? {
                    label: formState.currentValue[propertyName].toString(),
                    value: formState.currentValue[propertyName].toString(),
                  }
                : null
            }
          />
        );
      }
      case "multiselect<employee>":
        return (
          <FormFieldMultiSelectWrapper<AutoSuggestItem>
            values={formState.currentValue[propertyName]}
            getIdFromData={(data) => data.urlId}
            buildTokenFromData={(data) => ({
              label: data.title,
              description: `${getBusinessTitleFromSuggestItem(data)}, ${
                data.detail
              }`,
              dismissLabel: `Remove ${data.title}`,
            })}
            roundTokenImages
            onUpdateValues={(ids: AutoSuggestItem[]) => {
              dispatch(
                actions.setPropertyValueAction({
                  propertyName,
                  value: ids,
                })
              );
            }}
            render={(onSelect) => {
              return (
                <PeoplePicker
                  placeholder={placeholder}
                  clearOnSelection
                  disabled={!!disabled}
                  isSquare
                  searchQueryMinLength={3}
                  suggestionOnClick={onSelect}
                />
              );
            }}
          />
        );
      case "employee": {
        return (
          <PeoplePicker
            isSquare
            disabled={!!disabled}
            searchQueryMinLength={3}
            clear={() => {
              dispatch(
                actions.setPropertyValueAction({
                  propertyName,
                  value: undefined,
                })
              );
              dispatch(
                actions.setPropertyValueAction({
                  propertyName: `${propertyName}InputValue`,
                  value: undefined,
                })
              );
            }}
            selectedId={
              formState.currentValue[propertyName]
                ? formState.currentValue[propertyName].urlId
                : undefined
            }
            suggestionOnClick={(suggestion: AutoSuggestItem) => {
              dispatch(
                actions.setPropertyValueAction({
                  propertyName,
                  value: suggestion,
                })
              );
            }}
            placeholder={placeholder}
            onInputValueChange={(inputValue: string) => {
              dispatch(
                actions.setPropertyValueAction({
                  propertyName: `${propertyName}InputValue`,
                  value: inputValue,
                })
              );
            }}
          />
        );
      }
      case "team": {
        return (
          <TeamPicker
            isSquare
            disabled={!!disabled}
            searchQueryMinLength={3}
            clear={() => {
              dispatch(
                actions.setPropertyValueAction({
                  propertyName,
                  value: undefined,
                })
              );
            }}
            selectedId={formState.currentValue[propertyName]}
            suggestionOnClick={(suggestion: AutoSuggestItem) => {
              const teamId = getTeamIdFromSuggestItem(suggestion);
              dispatch(
                actions.setPropertyValueAction({
                  propertyName,
                  value: teamId,
                })
              );
            }}
            placeholder={placeholder}
          />
        );
      }
      case "string":
      case "number": {
        const type = field.propertyType === "string" ? "text" : "number";

        return (
          <FormFieldInputWrapper
            dispatchOnValueUpdate={dispatchOnValueUpdate || false}
            propertyName={propertyName}
            value={formState.currentValue[propertyName]}
            dispatch={dispatch}
            validationRules={validationRules}
          >
            <Input
              disabled={!!disabled}
              type={type}
              placeholder={placeholder}
              autoComplete={false}
              value={formState.currentValue[propertyName]}
            />
          </FormFieldInputWrapper>
        );
      }
      case "text":
        return (
          <FormFieldInputWrapper
            dispatchOnValueUpdate={dispatchOnValueUpdate || false}
            propertyName={propertyName}
            value={formState.currentValue[propertyName]}
            dispatch={dispatch}
          >
            <Textarea
              disabled={!!disabled}
              placeholder={placeholder}
              value={formState.currentValue[propertyName]}
            />
          </FormFieldInputWrapper>
        );
      default:
        throw new Error(`Invalid input type: "${field.propertyType}".`);
    }
  };

  const validationErrorTextFormatter =
    formState.customValidationErrorTextFormatter ||
    defaultValidationErrorTextFormatter;
  const generatedErrorText = validationErrorTextFormatter(
    formState.validationErrors,
    field.propertyName,
    formState.currentValue
  );

  const isOptional = !field.validationRules || !field.validationRules.presence;

  const ref: RefObject<HTMLElement> | null = formState.refCallback
    ? formState.refCallback(field.propertyName)
    : null;

  const label = !isOptional ? (
    <span ref={ref}>{field.label}</span>
  ) : (
    <span ref={ref}>
      {field.label}
      <i> - optional</i>
    </span>
  );

  return customLabel ? (
    <FormField
      label={label}
      errorText={generatedErrorText}
      stretch={stretch}
      description={customLabel}
    >
      {renderInput()}
    </FormField>
  ) : (
    <FormField label={label} errorText={generatedErrorText} stretch={stretch}>
      {renderInput()}
    </FormField>
  );
};

export default FormFieldRenderer;
